import { HttpEvent, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { $app } from '../../../app.service'
import {
   BudgetCategoryDOtherCosts,
   BudgetCategoryDOtherCostsResponse
} from '../../../features/feature-budget/models/interfaces/budget-category-d-data.interface'
import { BudgetCostsPerWp, BudgetCostsPerWpResponse } from '../../../features/feature-budget/models/interfaces/budget-costs-per-wp.interface'
import { BudgetEffort } from '../../../features/feature-budget/models/interfaces/budget-effort.interface'
import { BudgetExportFileType } from '../../../features/feature-budget/models/interfaces/budget-export.interface'
import { BudgetIncome, BudgetIncomeResponse } from '../../../features/feature-budget/models/interfaces/budget-income.interface'
import {
   BudgetJustificationOfCostsRequest,
   BudgetJustificationOfCostsResult
} from '../../../features/feature-budget/models/interfaces/budget-justification-of-costs.interface'
import { BudgetJustificationPartner } from '../../../features/feature-budget/models/interfaces/budget-justification-partner.interface'
import { BudgetSalary } from '../../../features/feature-budget/models/interfaces/budget-salary.interface'
import { BudgetStaffEffortResponse } from '../../../features/feature-budget/models/interfaces/budget-staff-effort.interface'
import { BudgetWorkPackage } from '../../../features/feature-budget/models/interfaces/budget-workpackage.interface'
import { BudgetWpWeightResponse } from '../../../features/feature-budget/models/interfaces/budget-wp-weight.interface'
import { AppBasicService } from '../../services/app-basic.service'

@Injectable()
export class BudgetService extends AppBasicService {
   getSalary(): Observable<BudgetSalary> {
      if (this.console) console.log(this.logPrefix, 'getSalary')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetSalary>(`${this._configService.apiUrl}/budgets/projects/${projectId}/salaryTable`)
   }

   updateSalary(data: BudgetSalary): Observable<BudgetSalary> {
      if (this.console) console.log(this.logPrefix, 'updateSalary - DATA', data)

      const projectId = $app.getCurrentProjectId()

      return this.http.put<BudgetSalary>(`${this._configService.apiUrl}/budgets/projects/${projectId}/salaryTable`, data)
   }

   getEffort(): Observable<BudgetEffort> {
      if (this.console) console.log(this.logPrefix, 'getEffort')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetEffort>(`${this._configService.apiUrl}/budgets/projects/${projectId}/effortTable`)
   }

   updateEffort(data: BudgetEffort): Observable<BudgetEffort> {
      if (this.console) console.log(this.logPrefix, 'updateEffort - DATA', data)

      const projectId = $app.getCurrentProjectId()

      return this.http.put<BudgetEffort>(`${this._configService.apiUrl}/budgets/projects/${projectId}/effortTable`, data)
   }

   getIncome(): Observable<BudgetIncomeResponse> {
      if (this.console) console.log(this.logPrefix, 'getIncome')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetIncomeResponse>(`${this._configService.apiUrl}/budgets/projects/${projectId}/expenditureTable`)
   }

   updateIncome(data: BudgetIncome[]): Observable<BudgetIncomeResponse> {
      if (this.console) console.log(this.logPrefix, 'updateIncome - DATA', data)

      const projectId = $app.getCurrentProjectId()

      return this.http.put<BudgetIncomeResponse>(`${this._configService.apiUrl}/budgets/projects/${projectId}/expenditureTable`, { partners: data })
   }

   getStaffEffort(): Observable<BudgetStaffEffortResponse> {
      if (this.console) console.log(this.logPrefix, 'getStaffEffort')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetStaffEffortResponse>(`${this._configService.apiUrl}/budgets/projects/${projectId}/summaryEffortTable`)
   }

   exportStaffEffort(fileType: BudgetExportFileType): Observable<HttpEvent<Blob>> {
      if (this.console) console.log(this.logPrefix, 'exportStaffEffort', fileType)

      const projectId = $app.getCurrentProjectId()
      const exportPath = this.getExportPathByFileType(fileType)
      return this.exportFile(`${this._configService.apiUrl}/budgets/projects/${projectId}/summaryEffortTable/${exportPath}`)
   }

   exportPpsForms(fileType: BudgetExportFileType): Observable<HttpEvent<Blob>> {
      if (this.console) console.log(this.logPrefix, 'exportPpsForms', fileType)

      const projectId = $app.getCurrentProjectId()
      const exportPath = this.getExportPathByFileType(fileType)
      return this.exportFile(`${this._configService.apiUrl}/budgets/projects/${projectId}/ppsFormTable/${exportPath}`)
   }

   getWpWeight(): Observable<BudgetWpWeightResponse> {
      if (this.console) console.log(this.logPrefix, 'getWpWeight')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetWpWeightResponse>(`${this._configService.apiUrl}/budgets/projects/${projectId}/wpWeightTable`)
   }

   exportWpWeight(fileType: BudgetExportFileType): Observable<HttpEvent<Blob>> {
      if (this.console) console.log(this.logPrefix, 'exportWpWeight', fileType)

      const projectId = $app.getCurrentProjectId()
      const exportPath = this.getExportPathByFileType(fileType)
      return this.exportFile(`${this._configService.apiUrl}/budgets/projects/${projectId}/wpWeightTable/${exportPath}`)
   }

   getWorkPackage(): Observable<BudgetWorkPackage[]> {
      if (this.console) console.log(this.logPrefix, 'getWorkPackage')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetWorkPackage[]>(`${this._configService.apiUrl}/workplans/workPackages/${projectId}`)
   }

   getCostsWp(wpId: number): Observable<BudgetCostsPerWpResponse> {
      if (this.console) console.log(this.logPrefix, 'getCostsWp', wpId)

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetCostsPerWpResponse>(`${this._configService.apiUrl}/budgets/projects/${projectId}/workpackages/${wpId}/expenditureTable`)
   }

   updateCostsWp(wpId: number, data: BudgetCostsPerWp[]): Observable<BudgetCostsPerWpResponse> {
      if (this.console) console.log(this.logPrefix, 'updateCostsWp - DATA', data)

      const projectId = $app.getCurrentProjectId()

      return this.http.put<BudgetCostsPerWpResponse>(`${this._configService.apiUrl}/budgets/projects/${projectId}/workpackages/${wpId}/expenditureTable`, {
         partners: data
      })
   }

   getPartner(): Observable<BudgetJustificationPartner[]> {
      if (this.console) console.log(this.logPrefix, 'getPartner')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetJustificationPartner[]>(`${this._configService.apiUrl}/budgets/projects/${projectId}/partners`)
   }

   updatePartnerStatus(partnerId: number, status: string): Observable<void> {
      if (this.console) console.log(this.logPrefix, 'updatePartnerStatus - STATUS', status)

      const projectId = $app.getCurrentProjectId()

      return this.http.put<void>(
         `${this._configService.apiUrl}/budgets/projects/${projectId}/partners/${partnerId}/statuses/justifications`,
         { status },
         this.httpOptions
      )
   }

   getBudgetJustificationOfCosts(partnerId: number): Observable<BudgetJustificationOfCostsResult> {
      if (this.console) console.log(this.logPrefix, 'getBudgetJustificationOfCosts', partnerId)

      const projectId = $app.getCurrentProjectId()

      return this.http.get<BudgetJustificationOfCostsResult>(
         `${this._configService.apiUrl}/budgets/projects/${projectId}/partners/${partnerId}/justificationTable`
      )
   }

   updateBudgetJustificationOfCosts(partnerId: number, data: BudgetJustificationOfCostsRequest[]): Observable<void> {
      if (this.console) console.log(this.logPrefix, 'updateBudgetJustificationOfCosts - DATA', data)

      const projectId = $app.getCurrentProjectId()

      return this.http.put<void>(`${this._configService.apiUrl}/budgets/projects/${projectId}/partners/${partnerId}/justificationTable`, data)
   }

   exportCostsJustif(fileType: BudgetExportFileType): Observable<HttpEvent<Blob>> {
      if (this.console) console.log(this.logPrefix, 'exportCostsJustif', fileType)

      const projectId = $app.getCurrentProjectId()
      const exportPath = this.getExportPathByFileType(fileType)
      return this.exportFile(`${this._configService.apiUrl}/budgets/projects/${projectId}/justificationTables/${exportPath}`)
   }

   getBudgetCategoryDOtherCosts(workpackageId?: number): Observable<BudgetCategoryDOtherCostsResponse> {
      if (this.console) console.log(this.logPrefix, 'getBudgetCategoryDOtherCosts')

      const projectId = $app.getCurrentProjectId()
      const endpoint = workpackageId
         ? `${this._configService.apiUrl}/budgets/projects/${projectId}/workpackages/${workpackageId}/expenditureTable/other-cost-categories`
         : `${this._configService.apiUrl}/budgets/projects/${projectId}/expenditureTable/other-cost-categories`

      return this.http.get<BudgetCategoryDOtherCostsResponse>(endpoint)
   }

   putBudgetCategoryDOtherCosts(workpackageId: number, data: Partial<BudgetCategoryDOtherCosts>[]): Observable<void> {
      if (this.console) console.log(this.logPrefix, 'putBudgetCategoryDOtherCosts - DATA', data)

      const projectId = $app.getCurrentProjectId()

      return this.http.put<void>(
         `${this._configService.apiUrl}/budgets/projects/${projectId}/workpackages/${workpackageId}/expenditureTable/other-cost-categories`,
         { partners: data }
      )
   }

   private getExportPathByFileType(fileType: BudgetExportFileType): string {
      switch (fileType) {
         case 'excel':
            return 'exportExcelFile'
         case 'word':
            return 'exportMsWordFile'
      }
   }

   private exportFile(url: string): Observable<HttpEvent<Blob>> {
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json'
         }),
         reportProgress: true,
         observe: 'events' as 'body',
         responseType: 'blob' as 'json'
      }

      return this.http.get(url, httpOptions) as Observable<HttpEvent<Blob>>
   }
}
